/**
 * @fileoverview Full manifest editor tab for legacy migration dataset configuration.
 * @module system-web/components/migration/ManifestEditorTab
 *
 * Responsibilities:
 * - Display the baseline manifest metadata (source tables, target collections) for a dataset.
 * - Show any active manifest override from Dexie (local-first liveQuery via useManifestOverride).
 * - Load and display live INFORMATION_SCHEMA data (online exception) via useLegacySchema.
 * - Compose ManifestTablesGrid, ManifestRelationshipsGrid, AddRelationshipModal,
 *   EditRelationshipModal, and ManifestDiffPanel into the full editor experience.
 * - Compute the effective relationship list locally from baseline + override for display.
 * - Commit override changes through useManifestOverride.save().
 *
 * Dependencies:
 * - lucide-react (icons)
 * - useManifestOverride hook
 * - useLegacySchema hook
 * - ManifestTablesGrid
 * - ManifestRelationshipsGrid
 * - AddRelationshipModal
 * - EditRelationshipModal
 * - ManifestDiffPanel
 *
 * Architecture:
 * - Override reads are local-first (Dexie liveQuery via useManifestOverride).
 * - Schema reads are online-exception (INFORMATION_SCHEMA via useLegacySchema).
 * - Relationship edits are staged locally in component state, then committed via
 *   useManifestOverride.save() which posts to system-api.
 * - The effective manifest view is computed from baseline props + current override state.
 * - liveQuery observed function performs ONLY reads — no writes (§liveQuery contract).
 *
 * Notes:
 * - A "pending" local draft of the override is maintained separately from the saved override.
 *   Changes are batched into a single save call when the admin clicks "Save Changes".
 * - The pending draft is initialised from the Dexie override on mount and whenever
 *   the override doc changes (e.g. after a successful save comes back via sync pull).
 *
 * @reusable
 * Usage: Rendered as the "Manifest" tab inside LegacyMigrationPage dataset tabs.
 * Rules: stateful (override + schema + draft state); receives datasetKey and baseline from parent.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, RotateCcw, Loader2, AlertTriangle, CheckCircle2,
  RefreshCw, Save,
} from 'lucide-react';
import { useManifestOverride } from '../../hooks/useManifestOverride';
import { useLegacySchema } from '../../hooks/useLegacySchema';
import { ManifestTablesGrid } from './manifest-editor/ManifestTablesGrid';
import { ManifestRelationshipsGrid } from './manifest-editor/ManifestRelationshipsGrid';
import { AddRelationshipModal } from './manifest-editor/AddRelationshipModal';
import { EditRelationshipModal } from './manifest-editor/EditRelationshipModal';
import { ManifestDiffPanel } from './manifest-editor/ManifestDiffPanel';
import ConfirmActionModal from '../ui/ConfirmActionModal';
import type { EffectiveRelationship } from './manifest-editor/ManifestRelationshipsGrid';
import type { AddRelationshipPayload } from './manifest-editor/AddRelationshipModal';
import type { EditRelationshipPayload } from './manifest-editor/EditRelationshipModal';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract BaselineRelationship
 *
 * Purpose:
 * - Minimal shape of a baseline manifest relationship, as provided by LegacyMigrationPage.
 *
 * Fields:
 * - matchKey: 'parentTable.parentCol -> childTable.childCol'
 * - parent, child: FK endpoints
 * - cardinality, enforcement, orphanPolicy, orphanPolicyJustification
 *
 * Rules:
 * - Provided by parent from the static ADAPTER_META constant.
 *
 * Sync:
 * - Not persisted; derived from adapter code.
 */
export interface BaselineRelationship {
  matchKey: string;
  parent: { table: string; column: string };
  child: { table: string; column: string };
  cardinality: 'one-to-one' | 'one-to-many';
  enforcement: 'declared' | 'convention' | 'inferred-by-name' | 'manifest-only';
  orphanPolicy: 'block' | 'warn' | 'drop' | 'includeAsRoot';
  orphanPolicyJustification?: string;
}

/**
 * @contract ManifestEditorTabProps
 *
 * Purpose:
 * - Props for the ManifestEditorTab component.
 *
 * Fields:
 * - datasetKey: Key of the dataset whose manifest to display.
 * - legacyDb: The legacy database name for live schema lookups.
 * - primaryTable: The baseline primaryTable name (for role badge).
 * - childTableNames: The set of baseline childTable names (for role badge).
 * - baselineRelationships: Baseline relationships from the adapter manifest.
 * - baselineTableCount: Number of source tables in the baseline manifest.
 * - baselineCollectionCount: Number of target collections in the baseline manifest.
 *
 * Rules:
 * - datasetKey must match a registered adapter key.
 * - legacyDb must be 'SLInspectionsDB001' | 'SLInspectionsDB002'.
 *
 * Sync:
 * - Override state from Dexie via useManifestOverride.
 * - Schema data from INFORMATION_SCHEMA via useLegacySchema (online exception).
 */
export interface ManifestEditorTabProps {
  datasetKey: string;
  legacyDb: string | null;
  primaryTable: string;
  childTableNames: Set<string>;
  baselineRelationships: BaselineRelationship[];
  baselineTableCount: number;
  baselineCollectionCount: number;
}

// ---------------------------------------------------------------------------
// Draft override shape
// ---------------------------------------------------------------------------

interface OverrideDraft {
  excludedTables: string[];
  addedRelationships: AddedRelationshipRecord[];
  modifiedRelationships: ModifiedRelationshipRecord[];
}

interface AddedRelationshipRecord {
  matchKey: string;
  parent: { table: string; column: string };
  child: { table: string; column: string };
  cardinality: 'one-to-one' | 'one-to-many';
  enforcement: 'manifest-only';
  orphanPolicy: 'block' | 'warn' | 'drop' | 'includeAsRoot';
  orphanPolicyJustification: string;
}

interface ModifiedRelationshipRecord {
  matchKey: string;
  orphanPolicy?: 'block' | 'warn' | 'drop' | 'includeAsRoot';
  orphanPolicyJustification?: string;
  cardinality?: 'one-to-one' | 'one-to-many';
  enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helper: compute effective relationships from baseline + draft
// ---------------------------------------------------------------------------

function computeEffectiveRelationships(
  baseline: BaselineRelationship[],
  draft: OverrideDraft,
): EffectiveRelationship[] {
  const excludedSet = new Set(draft.excludedTables);
  const modifiedMap = new Map(draft.modifiedRelationships.map((m) => [m.matchKey, m]));
  const results: EffectiveRelationship[] = [];

  // 1. Baseline relationships — apply modifications
  for (const rel of baseline) {
    const involvedTables = [rel.parent.table, rel.child.table];
    const tableExcluded = involvedTables.some((t) => excludedSet.has(t));
    const mod = modifiedMap.get(rel.matchKey);
    const isDisabled = !!mod && mod.enabled === false;

    results.push({
      matchKey: rel.matchKey,
      parent: rel.parent,
      child: rel.child,
      cardinality: (mod?.cardinality ?? rel.cardinality) as EffectiveRelationship['cardinality'],
      enforcement: rel.enforcement,
      orphanPolicy: (mod?.orphanPolicy ?? rel.orphanPolicy) as EffectiveRelationship['orphanPolicy'],
      orphanPolicyJustification: mod?.orphanPolicyJustification ?? rel.orphanPolicyJustification,
      isDisabled: isDisabled || tableExcluded,
      isModified: !!mod && !isDisabled,
      isAdded: false,
    });
  }

  // 2. Added relationships
  for (const added of draft.addedRelationships) {
    results.push({
      matchKey: added.matchKey,
      parent: added.parent,
      child: added.child,
      cardinality: added.cardinality,
      enforcement: 'manifest-only',
      orphanPolicy: added.orphanPolicy,
      orphanPolicyJustification: added.orphanPolicyJustification || undefined,
      isDisabled: false,
      isModified: false,
      isAdded: true,
    });
  }

  return results;
}

// ---------------------------------------------------------------------------
// Helper: derive a stable matchKey
// ---------------------------------------------------------------------------

function makeMatchKey(parentTable: string, parentCol: string, childTable: string, childCol: string): string {
  return `${parentTable}.${parentCol} -> ${childTable}.${childCol}`;
}

// ---------------------------------------------------------------------------
// Helper: initialise draft from a persisted override
// ---------------------------------------------------------------------------

function draftFromOverride(override: {
  excludedTables?: unknown;
  addedRelationships?: unknown;
  modifiedRelationships?: unknown;
} | null): OverrideDraft {
  return {
    excludedTables: (override?.excludedTables as string[] | undefined) ?? [],
    addedRelationships: (override?.addedRelationships as AddedRelationshipRecord[] | undefined) ?? [],
    modifiedRelationships: (override?.modifiedRelationships as ModifiedRelationshipRecord[] | undefined) ?? [],
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component ManifestEditorTab
 *
 * Purpose:
 * - Full manifest editor for configuring the effective dataset manifest via an override.
 *
 * Props:
 * - See ManifestEditorTabProps.
 *
 * Behavior:
 * - Left panel: live schema tables (ManifestTablesGrid) with exclude/include toggles.
 * - Right panel: effective relationships (ManifestRelationshipsGrid) with edit/disable buttons.
 * - Below both: ManifestDiffPanel showing override summary and version history.
 * - Toolbar: Load Schema | Save Changes | Reset to Baseline.
 * - Save Changes submits the accumulated draft via useManifestOverride.save().
 * - Unsaved local changes are indicated with a "Unsaved changes" chip in the toolbar.
 * - Schema is loaded on first "Load Schema" click; not automatic to avoid unnecessary SQL.
 *
 * Data Source:
 * - local (Dexie via useManifestOverride) + remote (online-exception SQL via useLegacySchema)
 *
 * Constraints:
 * - No direct apiFetch calls; all mutations go through useManifestOverride or useLegacySchema.
 * - liveQuery observed function performs ONLY reads (§liveQuery contract).
 *
 * Performance:
 * - Schema loaded on-demand (not on mount).
 * - Column details fetched lazily on row expand.
 *
 * @reusable
 * Usage: One per dataset tab inside LegacyMigrationPage.
 * Rules: stateful (override + schema + draft state).
 */
export function ManifestEditorTab({
  datasetKey,
  legacyDb,
  primaryTable,
  childTableNames,
  baselineRelationships,
  baselineTableCount,
  baselineCollectionCount,
}: ManifestEditorTabProps) {
  const { override, isLoading: overrideLoading, isSaving, error: overrideError, save, reset } = useManifestOverride(datasetKey);
  const schema = useLegacySchema(legacyDb);

  // Local draft — staged changes not yet sent to the server
  const [draft, setDraft] = useState<OverrideDraft>(() => draftFromOverride(null));
  const [isDirty, setIsDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRel, setEditingRel] = useState<EffectiveRelationship | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  // Sync draft from persisted override when it arrives or changes (e.g. after save via sync pull)
  useEffect(() => {
    if (override !== undefined) {
      setDraft(draftFromOverride(override));
      setIsDirty(false);
      setSaveError(null);
    }
  }, [override]);

  // Compute the effective relationships for display
  const effectiveRelationships = useMemo(
    () => computeEffectiveRelationships(baselineRelationships, draft),
    [baselineRelationships, draft],
  );

  // ---------------------------------------------------------------------------
  // Exclude / include table
  // ---------------------------------------------------------------------------
  const handleExcludeChange = useCallback((table: string, excluded: boolean) => {
    setDraft((prev) => {
      const current = new Set(prev.excludedTables);
      if (excluded) { current.add(table); } else { current.delete(table); }
      return { ...prev, excludedTables: Array.from(current) };
    });
    setIsDirty(true);
    setSaveError(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Disable a relationship
  // ---------------------------------------------------------------------------
  const handleDisable = useCallback((rel: EffectiveRelationship) => {
    if (rel.isAdded) {
      // Remove from addedRelationships
      setDraft((prev) => ({
        ...prev,
        addedRelationships: prev.addedRelationships.filter((r) => r.matchKey !== rel.matchKey),
      }));
    } else {
      // Add or update modifiedRelationships with enabled: false
      setDraft((prev) => {
        const existing = prev.modifiedRelationships.find((m) => m.matchKey === rel.matchKey);
        if (existing) {
          return {
            ...prev,
            modifiedRelationships: prev.modifiedRelationships.map((m) =>
              m.matchKey === rel.matchKey ? { ...m, enabled: false } : m,
            ),
          };
        }
        return {
          ...prev,
          modifiedRelationships: [...prev.modifiedRelationships, { matchKey: rel.matchKey, enabled: false }],
        };
      });
    }
    setIsDirty(true);
    setSaveError(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Add relationship
  // ---------------------------------------------------------------------------
  const handleAddConfirm = useCallback(async (payload: AddRelationshipPayload) => {
    const matchKey = makeMatchKey(payload.parentTable, payload.parentColumn, payload.childTable, payload.childColumn);
    const newRel: AddedRelationshipRecord = {
      matchKey,
      parent: { table: payload.parentTable, column: payload.parentColumn },
      child: { table: payload.childTable, column: payload.childColumn },
      cardinality: payload.cardinality,
      enforcement: 'manifest-only',
      orphanPolicy: payload.orphanPolicy,
      orphanPolicyJustification: payload.orphanPolicyJustification,
    };
    setDraft((prev) => ({
      ...prev,
      addedRelationships: [...prev.addedRelationships, newRel],
    }));
    setIsDirty(true);
    setSaveError(null);
    setShowAddModal(false);
  }, []);

  // ---------------------------------------------------------------------------
  // Edit relationship
  // ---------------------------------------------------------------------------
  const handleEditConfirm = useCallback(async (payload: EditRelationshipPayload) => {
    const rel = effectiveRelationships.find((r) => r.matchKey === payload.matchKey);
    if (!rel) return;

    if (rel.isAdded) {
      // Update directly in addedRelationships
      setDraft((prev) => ({
        ...prev,
        addedRelationships: prev.addedRelationships.map((r) =>
          r.matchKey === payload.matchKey
            ? {
                ...r,
                cardinality: payload.cardinality,
                orphanPolicy: payload.orphanPolicy,
                orphanPolicyJustification: payload.orphanPolicyJustification,
              }
            : r,
        ),
      }));
    } else {
      // Upsert in modifiedRelationships
      setDraft((prev) => {
        const existing = prev.modifiedRelationships.find((m) => m.matchKey === payload.matchKey);
        const modification: ModifiedRelationshipRecord = {
          ...(existing ?? {}),
          matchKey: payload.matchKey,
          orphanPolicy: payload.orphanPolicy,
          orphanPolicyJustification: payload.orphanPolicyJustification,
          // cardinality only modifiable for added rels; for baseline it's read-only
        };
        if (existing) {
          return {
            ...prev,
            modifiedRelationships: prev.modifiedRelationships.map((m) =>
              m.matchKey === payload.matchKey ? modification : m,
            ),
          };
        }
        return {
          ...prev,
          modifiedRelationships: [...prev.modifiedRelationships, modification],
        };
      });
    }
    setIsDirty(true);
    setSaveError(null);
    setEditingRel(null);
  }, [effectiveRelationships]);

  // ---------------------------------------------------------------------------
  // Save draft
  // ---------------------------------------------------------------------------
  const handleSave = useCallback(async () => {
    setSaveError(null);
    try {
      await save({
        excludedTables: draft.excludedTables,
        addedRelationships: draft.addedRelationships as unknown[],
        modifiedRelationships: draft.modifiedRelationships as unknown[],
        version: (override?.version ?? 0) + 1,
      });
      setIsDirty(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save manifest override');
    }
  }, [draft, override, save]);

  // ---------------------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------------------
  const handleReset = useCallback(() => {
    setConfirmReset(true);
  }, []);

  const doReset = useCallback(async () => {
    setConfirmReset(false);
    setSaveError(null);
    try {
      await reset();
      setDraft(draftFromOverride(null));
      setIsDirty(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to reset manifest override');
    }
  }, [reset]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const hasActiveOverride = override && !override._deleted;

  if (overrideLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm opacity-60">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading manifest…</span>
      </div>
    );
  }

  const error = overrideError ?? saveError;

  return (
    <div className="space-y-4 py-2">

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">

        {/* Left: override status */}
        <div className="flex items-center gap-2">
          {hasActiveOverride ? (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
              <AlertTriangle className="h-3 w-3" />
              Override active (v{override.version})
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
              <CheckCircle2 className="h-3 w-3" />
              Using baseline
            </span>
          )}
          {isDirty && (
            <span className="text-xs text-amber-600 font-medium">
              · Unsaved changes
            </span>
          )}
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2">
          {!schema.tables.length && (
            <button
              onClick={() => schema.loadTables()}
              disabled={schema.isLoadingTables}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {schema.isLoadingTables
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5" />
              }
              Load Schema
            </button>
          )}
          {schema.tables.length > 0 && (
            <button
              onClick={() => schema.loadTables()}
              disabled={schema.isLoadingTables}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              title="Refresh live schema from INFORMATION_SCHEMA"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${schema.isLoadingTables ? 'animate-spin' : ''}`} />
              Refresh Schema
            </button>
          )}
          {isDirty && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50"
              style={{ background: '#2563eb' }}
            >
              {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save Changes
            </button>
          )}
          {hasActiveOverride && !isDirty && (
            <button
              onClick={handleReset}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
              Reset to Baseline
            </button>
          )}
        </div>
      </div>

      {/* Baseline summary */}
      <div className="rounded-lg border px-4 py-3 space-y-1" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <div className="flex items-center gap-2 text-sm font-semibold">
          <FileText className="h-4 w-4 opacity-60" />
          Baseline Manifest
        </div>
        <dl className="grid grid-cols-2 gap-x-6 text-sm text-gray-600 mt-1">
          <dt className="font-medium">Source tables</dt>
          <dd>{baselineTableCount}</dd>
          <dt className="font-medium">Target collections</dt>
          <dd>{baselineCollectionCount}</dd>
          <dt className="font-medium">Relationships</dt>
          <dd>{baselineRelationships.length}</dd>
        </dl>
      </div>

      {/* Tables grid */}
      <div>
        <h3 className="text-sm font-semibold mb-2">Tables</h3>
        <ManifestTablesGrid
          tables={schema.tables}
          primaryTable={primaryTable}
          childTableNames={childTableNames}
          excludedTables={draft.excludedTables}
          isLoading={schema.isLoadingTables}
          error={schema.tablesError}
          onExcludeChange={handleExcludeChange}
          fetchColumnsFor={schema.fetchColumnsFor}
          columnsCache={schema.columnsCache}
        />
      </div>

      {/* Relationships grid */}
      <div>
        <h3 className="text-sm font-semibold mb-2">Relationships</h3>
        <ManifestRelationshipsGrid
          relationships={effectiveRelationships}
          onEdit={(rel) => setEditingRel(rel)}
          onDisable={handleDisable}
          onAddNew={() => setShowAddModal(true)}
        />
      </div>

      {/* Diff panel */}
      <ManifestDiffPanel
        override={override}
        baselineRelationshipCount={baselineRelationships.length}
        baselineTableCount={baselineTableCount}
      />

      {/* Add relationship modal */}
      {showAddModal && (
        <AddRelationshipModal
          tables={schema.tables}
          fetchColumnsFor={schema.fetchColumnsFor}
          onConfirm={handleAddConfirm}
          onCancel={() => setShowAddModal(false)}
        />
      )}

      {/* Edit relationship modal */}
      {editingRel && (
        <EditRelationshipModal
          relationship={editingRel}
          onConfirm={handleEditConfirm}
          onCancel={() => setEditingRel(null)}
        />
      )}

      {/* Reset to baseline confirmation */}
      {confirmReset && (
        <ConfirmActionModal
          title="Reset to Baseline?"
          description="This will discard the manifest override. The effective manifest will revert to the baseline configuration."
          confirmButtonLabel="Reset to Baseline"
          variant="warning"
          onConfirm={doReset}
          onClose={() => setConfirmReset(false)}
        />
      )}
    </div>
  );
}
